import React from 'react';
import FinexLogo from '../../components/FinexLogo';

/**
 * Printable / downloadable Purchase Order document.
 *
 * Language-driven via `lang` ('al' | 'en'), self-contained inline styles so it
 * renders identically through window.print() and html2pdf.
 *
 * Laid out to fill an A4 page: a min-height flex column pushes the signatures
 * and footer to the bottom instead of leaving the lower half of the page blank.
 *
 * Party data:
 *   - From      → dbo.companies (issuer)
 *   - Supplier  → dbo.clients
 *   - Deliver to → dbo.locations
 */

export interface OrderPrintRow {
  sku?: string;
  productName?: string;
  uomName?: string;
  orderQty?: number;
  cost?: number;
  lineCost?: number;
}

/** Company / supplier address block (companies + clients share this shape). */
export interface PartyDetails {
  companyName?: string | null;
  companyNumber?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  postalCode?: string | null;
}

export interface OrderPrintViewProps {
  lang?: 'al' | 'en';
  company?: PartyDetails | null;
  supplier?: PartyDetails | null;
  location?: { name?: string | null; address?: string | null } | null;
  orderNumber?: string | number | null;
  date?: string | null;
  statusId?: number | null;
  orderedBy?: string | null;
  approvedBy?: string | null;
  approvalDate?: string | null;
  vat?: number;
  rows?: OrderPrintRow[];
}

const LABELS = {
  en: {
    title: 'PURCHASE ORDER',
    from: 'From',
    date: 'Date',
    orderNumber: 'Order number',
    supplier: 'Supplier',
    deliverTo: 'Deliver to',
    no: 'No.',
    sku: 'SKU',
    product: 'Product',
    uom: 'UOM',
    qty: 'Qty',
    unitCost: 'Unit cost',
    total: 'Total',
    subtotal: 'Subtotal',
    vat: 'VAT',
    grandTotal: 'Total due',
    orderedBy: 'Ordered by',
    approvedBy: 'Approved by',
    noItems: 'No items on this order.',
  },
  al: {
    title: 'POROSI BLERJEJE',
    from: 'Nga',
    date: 'Data',
    orderNumber: 'Numri i porosisë',
    supplier: 'Furnitori',
    deliverTo: 'Dorëzo te',
    no: 'Nr.',
    sku: 'SKU',
    product: 'Produkti',
    uom: 'Njësia',
    qty: 'Sasia',
    unitCost: 'Çmimi për njësi',
    total: 'Totali',
    subtotal: 'Nën-total',
    vat: 'TVSH',
    grandTotal: 'Për pagesë',
    orderedBy: 'Porositur nga',
    approvedBy: 'Aprovuar nga',
    noItems: 'Nuk ka artikuj në këtë porosi.',
  },
};

const STATUS: Record<'al' | 'en', Record<number, string>> = {
  en: { 1: 'Draft', 2: 'Saved', 3: 'Approved', 4: 'Rejected', 8: 'Delivered', 9: 'Cancelled' },
  al: { 1: 'Skicë', 2: 'Ruajtur', 3: 'Aprovuar', 4: 'Refuzuar', 8: 'Dorëzuar', 9: 'Anuluar' },
};

// Palette — restrained, one teal accent (the existing brand colour).
const ACCENT = '#0F7679';
const ACCENT_SOFT = '#EAF3F3';
const INK = '#1F2937';
const MUTED = '#6B7280';
const LINE = '#E6E8EB';

const join = (parts: Array<string | null | undefined>) =>
  parts.map((p) => (p == null ? '' : String(p).trim())).filter(Boolean).join(', ');

const OrderPrintView: React.FC<OrderPrintViewProps> = ({
  lang = 'al',
  company,
  supplier,
  location,
  orderNumber,
  date,
  statusId,
  orderedBy,
  approvedBy,
  approvalDate,
  vat = 0,
  rows = [],
}) => {
  const t = LABELS[lang];
  const locale = lang === 'al' ? 'de-DE' : 'en-US';
  const money = (n: number) =>
    `${new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0)} €`;
  const qty = (n: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(Number(n) || 0);

  const statusLabel = statusId ? STATUS[lang][statusId] ?? '' : '';
  const detailRows = Array.isArray(rows)
    ? rows.filter((r) => r && (r.productName || r.sku))
    : [];

  const subtotal = detailRows.reduce((acc, r) => acc + (Number(r.lineCost) || 0), 0);
  const vatAmount = Number(vat) || 0;
  const grandTotal = subtotal + vatAmount;
  const orderRef = orderNumber != null && orderNumber !== '' ? `#${orderNumber}` : '–';

  const metaLabel: React.CSSProperties = {
    fontSize: '10px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: MUTED,
    fontWeight: 600,
  };
  const th: React.CSSProperties = { ...metaLabel, padding: '10px 8px', textAlign: 'left' };
  const thRight: React.CSSProperties = { ...th, textAlign: 'right' };
  const td: React.CSSProperties = {
    padding: '11px 8px',
    color: INK,
    borderBottom: `1px solid ${LINE}`,
    verticalAlign: 'top',
  };
  const tdRight: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  // Company / supplier block: name (bold), number, address lines, city + postcode.
  const partyBlock = (p?: PartyDetails | null, nameSize = '14px') => {
    const addr = join([p?.addressLine1, p?.addressLine2]);
    const cityLine = join([p?.city, p?.postalCode]);
    return (
      <>
        <div style={{ fontWeight: 700, fontSize: nameSize, color: INK }}>
          {p?.companyName || '–'}
        </div>
        {p?.companyNumber && <div style={{ color: INK, fontSize: '12px' }}>{p.companyNumber}</div>}
        {addr && <div style={{ color: MUTED, fontSize: '12px' }}>{addr}</div>}
        {cityLine && <div style={{ color: MUTED, fontSize: '12px' }}>{cityLine}</div>}
      </>
    );
  };

  return (
    <div
      style={{
        width: '760px',
        minHeight: '1040px',
        boxSizing: 'border-box',
        padding: '44px 44px 32px',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Inter, Helvetica, Arial, sans-serif',
        fontSize: '13px',
        lineHeight: 1.45,
        color: INK,
        background: '#fff',
      }}
    >
      {/* Letterhead: issuer ("From" = company) left + document meta right */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          paddingBottom: '20px',
          borderBottom: `2px solid ${ACCENT}`,
        }}
      >
        <div style={{ maxWidth: '58%' }}>
          <div style={{ ...metaLabel, marginBottom: '6px' }}>{t.from}</div>
          {partyBlock(company, '20px')}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700, fontSize: '15px', color: ACCENT, letterSpacing: '0.04em' }}>
            {t.title}
          </div>
          <div style={{ marginTop: '8px', fontSize: '12px' }}>
            <div style={{ color: MUTED }}>
              {t.orderNumber}: <span style={{ color: INK, fontWeight: 700 }}>{orderRef}</span>
            </div>
            <div style={{ color: MUTED, marginTop: '2px' }}>
              {t.date}: <span style={{ color: INK, fontWeight: 600 }}>{date ?? '–'}</span>
            </div>
            {statusLabel && (
              <div style={{ marginTop: '6px' }}>
                <span
                  style={{
                    display: 'inline-block',
                    padding: '2px 10px',
                    borderRadius: '999px',
                    backgroundColor: ACCENT_SOFT,
                    color: ACCENT,
                    fontSize: '11px',
                    fontWeight: 700,
                  }}
                >
                  {statusLabel}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Parties: supplier (client) + deliver-to (location) */}
      <div style={{ display: 'flex', gap: '32px', marginTop: '26px', marginBottom: '24px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ ...metaLabel, marginBottom: '6px' }}>{t.supplier}</div>
          {partyBlock(supplier)}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...metaLabel, marginBottom: '6px' }}>{t.deliverTo}</div>
          <div style={{ fontWeight: 700, fontSize: '14px', color: INK }}>{location?.name || '–'}</div>
          {location?.address && (
            <div style={{ color: MUTED, fontSize: '12px' }}>{location.address}</div>
          )}
        </div>
      </div>

      {/* Line table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', border: `1px solid ${LINE}` }}>
        <thead>
          <tr style={{ backgroundColor: '#F7F8F9', borderBottom: `1px solid ${LINE}` }}>
            <th style={{ ...th, width: '30px' }}>{t.no}</th>
            <th style={{ ...th, width: '78px' }}>{t.sku}</th>
            <th style={th}>{t.product}</th>
            <th style={{ ...th, width: '54px' }}>{t.uom}</th>
            <th style={{ ...thRight, width: '46px' }}>{t.qty}</th>
            <th style={{ ...thRight, width: '82px' }}>{t.unitCost}</th>
            <th style={{ ...thRight, width: '86px' }}>{t.total}</th>
          </tr>
        </thead>
        <tbody>
          {detailRows.length === 0 ? (
            <tr>
              <td style={{ ...td, textAlign: 'center', color: MUTED }} colSpan={7}>
                {t.noItems}
              </td>
            </tr>
          ) : (
            detailRows.map((row, idx) => (
              <tr key={idx} style={{ backgroundColor: idx % 2 === 1 ? '#FBFBFC' : '#fff' }}>
                <td style={{ ...td, color: MUTED }}>{idx + 1}</td>
                <td style={td}>{row.sku ?? '–'}</td>
                <td style={{ ...td, fontWeight: 600 }}>{row.productName ?? '–'}</td>
                <td style={{ ...td, color: MUTED }}>{row.uomName ?? '–'}</td>
                <td style={tdRight}>{qty(Number(row.orderQty ?? 0))}</td>
                <td style={tdRight}>{money(Number(row.cost ?? 0))}</td>
                <td style={{ ...tdRight, fontWeight: 600 }}>{money(Number(row.lineCost ?? 0))}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Totals */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '280px', fontVariantNumeric: 'tabular-nums' }}>
          <tbody>
            <tr>
              <td style={{ padding: '5px 0', color: MUTED }}>{t.subtotal}</td>
              <td style={{ padding: '5px 0', textAlign: 'right', color: INK }}>{money(subtotal)}</td>
            </tr>
            {vatAmount > 0 && (
              <tr>
                <td style={{ padding: '5px 0', color: MUTED }}>{t.vat}</td>
                <td style={{ padding: '5px 0', textAlign: 'right', color: INK }}>{money(vatAmount)}</td>
              </tr>
            )}
            <tr>
              <td style={{ padding: '10px 0 0', borderTop: `2px solid ${ACCENT}`, fontWeight: 700, fontSize: '15px' }}>
                {t.grandTotal}
              </td>
              <td
                style={{
                  padding: '10px 0 0',
                  borderTop: `2px solid ${ACCENT}`,
                  textAlign: 'right',
                  fontWeight: 700,
                  fontSize: '15px',
                  color: ACCENT,
                }}
              >
                {money(grandTotal)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Spacer — pushes signatures + footer to the bottom of the page */}
      <div style={{ flex: 1, minHeight: '48px' }} />

      {/* Authorization */}
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '40px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ height: '24px', fontWeight: 600 }}>{orderedBy ?? ''}</div>
          <div style={{ borderTop: `1px solid ${INK}`, paddingTop: '6px', ...metaLabel }}>{t.orderedBy}</div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ height: '24px', fontWeight: 600 }}>
            {approvedBy ?? ''}
            {approvedBy && approvalDate ? `  ·  ${approvalDate}` : ''}
          </div>
          <div style={{ borderTop: `1px solid ${INK}`, paddingTop: '6px', ...metaLabel }}>{t.approvedBy}</div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '28px',
          paddingTop: '12px',
          borderTop: `1px solid ${LINE}`,
          fontSize: '10px',
          color: MUTED,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FinexLogo height={15} />
          <span>www.meetfinex.com</span>
        </span>
        <span>1 / 1</span>
      </div>
    </div>
  );
};

export default OrderPrintView;
